/* /admin/tenants — customer companies, trial extension (prototype billing.js pgAdminTenants). */
import { COUNTRIES, countryOf } from "../../domain/country";
import { industryOf } from "../../domain/industry";
import { useMemo, useState } from "react";
import { useAdminTenants, useExtendTrial } from "../../data/hooks";
import type { TenantSummary } from "../../data/types";
import { useI18n } from "../../i18n";
import { fmtN, gDate } from "../../lib/format";
import { Button, Card, EmptyState, Loaded, PageHeader, SearchBox, Select, errMsg, useToast } from "../../ui";

const STATUS_AR: Record<string, string> = { Active: "نشط", Trial: "تجربة", "Payment review": "مراجعة الدفع", "Trial ended": "انتهت التجربة" };
const toneOf = (s: string) => (s === "Active" ? "ok" : s === "Trial" ? "gold" : s === "Payment review" ? "warn" : "bad");

export function TenantStatusChip({ status }: { status: string }) {
  const { L } = useI18n();
  return <span className={"chip dot " + toneOf(status)}>{L(status, STATUS_AR[status] ?? status)}</span>;
}

export default function AdminTenants() {
  const { t, L, lang } = useI18n();
  const toast = useToast();
  const q = useAdminTenants();
  const extend = useExtendTrial();
  const [s, setS] = useState("");
  const [cc, setCc] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const doExtend = async (x: TenantSummary) => {
    setBusy(x.id);
    try { await extend.mutateAsync({ tenantId: x.id, days: 7 }); toast(L("Trial extended by 7 days for " + x.name, "تم تمديد التجربة 7 أيام لـ " + x.name)); }
    catch (e) { toast(errMsg(e), "bad"); }
    finally { setBusy(null); }
  };
  const filter = useMemo(() => (list: TenantSummary[]) => {
    const k = s.trim().toLowerCase();
    const inCountry = cc ? list.filter((x) => countryOf(x.country).code === cc) : list;
    return k ? inCountry.filter((x) => (x.name + " " + x.cr + " " + x.city + " " + (industryOf(x.category)?.name.join(" ") ?? "") + " " + countryOf(x.country).name.join(" ")).toLowerCase().includes(k)) : inCountry;
  }, [s, cc]);
  return (
    <Loaded q={q}>{(all) => {
      const list = filter(all);
      return (
        <div data-testid="admin-tenants">
          <PageHeader title={L("Companies", "الشركات")} sub={all.length + " " + L("companies", "منشأة")} />
          <Card flush>
            <div className="toolbar" style={{ padding: 12 }}><SearchBox value={s} onChange={setS} placeholder={L("Search company, CR or city", "ابحث بالمنشأة أو السجل أو المدينة")} testId="tenant-search" />
              <Select value={cc} onChange={(e) => setCc(e.target.value)} aria-label={L("Country", "الدولة")} data-testid="tenant-country"
                options={[["", L("All countries", "كل الدول")], ...COUNTRIES.map((c): [string, string] => [c.code, c.flag + " " + L(...c.name)])]} /></div>
            {list.length === 0 ? <EmptyState title={L("No companies match", "لا توجد نتائج")} desc={L("Try another name or CR number.", "جرّب اسمًا أو رقم سجل آخر.")} /> : (
              <div className="tw">
                <table className="tbl cards" data-testid="tenants-table">
                  <thead><tr><th>{L("Company", "المنشأة")}</th><th className="hide-md">CR</th><th>{L("Plan", "الباقة")}</th><th className="end">{t("Employees")}</th><th className="end hide-md">{L("AI credits (month)", "رصيد الذكاء")}</th><th>{t("Status")}</th><th className="end" aria-label={t("Actions")} /></tr></thead>
                  <tbody>
                    {list.map((x) => (
                      <tr key={x.id} data-testid={"tenant-" + x.id}>
                        <td className="k1"><b>{x.name}</b><div className="xs muted"><span data-testid="tenant-country-flag" title={L(...countryOf(x.country).name)}>{countryOf(x.country).flag}</span> {x.city} · {L("since ", "منذ ")}{gDate(x.since, lang)}</div>
                          <div className="xs muted" data-testid="tenant-category">{industryOf(x.category) ? L(...industryOf(x.category)!.name) : L("No category yet", "لم يُحدد النشاط")}</div></td>
                        <td className="hide-md num">{x.cr}</td>
                        <td className="x" data-l={L("Plan", "الباقة")}>{x.plan}</td>
                        <td className="end k3" data-l={t("Employees")}><span className="num">{x.seats}</span></td>
                        <td className="end hide-md num">{fmtN(x.aiCredits, 0)}</td>
                        <td className="k2"><TenantStatusChip status={x.status} /></td>
                        <td className="end k3" data-l="">{/Trial/.test(x.status) && <Button variant="ghost sm" loading={busy === x.id} onClick={() => doExtend(x)} data-testid={"btn-extend-" + x.id}>{L("Extend trial 7 days", "تمديد التجربة 7 أيام")}</Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      );
    }}</Loaded>
  );
}
